import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { MdOutlineCalendarMonth } from 'react-icons/md';

import { FILTER_ROSE_BG, FILTER_ROSE_DARK } from '../feed-filter-bar/filter-trigger-button';
import { ReviewFilterShell } from './review-filter-shell';
import type { TReviewDateField, TReviewDateRange } from './review-search';

interface TPreset {
  id: string;
  label: string;
  range: () => { from?: DateTime; to?: DateTime };
}

const startOfToday = () => DateTime.now().startOf('day');

/** Looking back: when something was submitted, reviewed, rejected or started. */
const PAST_PRESETS: TPreset[] = [
  { id: 'today', label: 'Today', range: () => ({ from: startOfToday(), to: startOfToday().endOf('day') }) },
  { id: '7d', label: 'Last 7 days', range: () => ({ from: startOfToday().minus({ days: 6 }), to: undefined }) },
  { id: '30d', label: 'Last 30 days', range: () => ({ from: startOfToday().minus({ days: 29 }), to: undefined }) },
  { id: 'month', label: 'This month', range: () => ({ from: startOfToday().startOf('month'), to: undefined }) },
];

/** Due dates also look ahead. */
const DUE_PRESETS: TPreset[] = [
  { id: 'past', label: 'Already due', range: () => ({ from: undefined, to: DateTime.now() }) },
  {
    id: 'next7',
    label: 'Next 7 days',
    range: () => ({ from: DateTime.now(), to: startOfToday().plus({ days: 7 }).endOf('day') }),
  },
  {
    id: 'month',
    label: 'This month',
    range: () => ({ from: startOfToday().startOf('month'), to: startOfToday().endOf('month') }),
  },
];

const toIso = (value: DateTime | undefined) => (value ? (value.toUTC().toISO() ?? undefined) : undefined);
const fromIso = (value: string | undefined) => (value ? DateTime.fromISO(value) : null);
const short = (value: DateTime) => value.toLocaleString({ day: 'numeric', month: 'short' });

/** "14 Oct – 16 Oct", "From 14 Oct", "Until 16 Oct". */
const rangeText = (range: TReviewDateRange): string => {
  const from = fromIso(range.from);
  const to = fromIso(range.to);
  if (from && to) {
    return from.hasSame(to, 'day') ? short(from) : `${short(from)} – ${short(to)}`;
  }
  return from ? `From ${short(from)}` : to ? `Until ${short(to)}` : '';
};

interface ReviewDateFilterProps {
  /** The date columns this view can filter on. */
  fields: Array<{ value: TReviewDateField; label: string }>;
  value: TReviewDateRange;
  onChange: (value: TReviewDateRange) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Filter on a date range: pick which date (submitted, feedback sent, due…), then a
 * quick range or your own from / to. Changes apply at once; Done only closes.
 */
export const ReviewDateFilter: FC<ReviewDateFilterProps> = ({ fields, value, onChange, open, onOpenChange }) => {
  const field = fields.find(f => f.value === value.field) ?? fields[0];
  const isActive = !!value.from || !!value.to;
  const presets = field.value === 'due' ? DUE_PRESETS : PAST_PRESETS;
  const from = fromIso(value.from);
  const to = fromIso(value.to);

  const isPresetOn = (preset: TPreset) => {
    const range = preset.range();
    const sameEnd = (a: DateTime | null, b: DateTime | undefined) => (!a && !b) || (!!a && !!b && a.hasSame(b, 'day'));
    return isActive && sameEnd(from, range.from) && sameEnd(to, range.to);
  };

  const setRange = (next: { from?: DateTime; to?: DateTime }) =>
    onChange({ field: field.value, from: toIso(next.from), to: toIso(next.to) });

  const onDateInput = (end: 'from' | 'to', text: string) => {
    const day = text ? DateTime.fromISO(text) : undefined;
    setRange({
      from: end === 'from' ? day?.startOf('day') : (from ?? undefined),
      to: end === 'to' ? day?.endOf('day') : (to ?? undefined),
    });
  };

  return (
    <ReviewFilterShell
      label="Date"
      icon={<MdOutlineCalendarMonth />}
      selectedLabels={isActive ? [`${field.label} ${rangeText(value)}`] : []}
      mode="include"
      open={open}
      onOpenChange={onOpenChange}
    >
      <Box sx={{ px: 2, pt: 1.75, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography sx={{ fontFamily: 'Figtree, sans-serif', fontWeight: 700, fontSize: 15 }}>
          Filter by date
        </Typography>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={field.value}
          onChange={(_, next: TReviewDateField | null) => next && onChange({ ...value, field: next })}
          aria-label="Which date"
          sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, py: 0.5 } }}
        >
          {fields.map(f => (
            <ToggleButton key={f.value} value={f.value}>
              {f.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {presets.map(preset => {
            const isOn = isPresetOn(preset);
            return (
              <Chip
                key={preset.id}
                label={preset.label}
                clickable
                aria-pressed={isOn}
                onClick={() => setRange(preset.range())}
                sx={{
                  fontWeight: 600,
                  bgcolor: isOn ? FILTER_ROSE_BG : undefined,
                  color: isOn ? FILTER_ROSE_DARK : undefined,
                }}
              />
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={from?.toISODate() ?? ''}
            onChange={e => onDateInput('from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: to?.toISODate() ?? undefined }}
            fullWidth
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={to?.toISODate() ?? ''}
            onChange={e => onDateInput('to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: from?.toISODate() ?? undefined }}
            fullWidth
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 1.25,
          pb: 'calc(10px + env(safe-area-inset-bottom))',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          size="small"
          variant="text"
          disabled={!isActive}
          onClick={() => onChange({ field: field.value })}
          sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
        >
          Clear
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => onOpenChange(false)}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', boxShadow: 'none', px: 2.5 }}
        >
          Done
        </Button>
      </Box>
    </ReviewFilterShell>
  );
};
